import { useEffect, useMemo, useState } from 'react';
import type { Tenant, Order, OrderItem } from '../../lib/database.types';
import { supabase } from '../../lib/supabase';
import { loadOrders } from '../../lib/orders';
import { formatPrice } from '../../lib/cart';
import { computeDashboardStats, deltaText, type DashboardStats } from '../../lib/dashboardStats';

/**
 * Tableau de bord SaaS (parité legacy BB/FK) — 100 % données RÉELLES, aucun chiffre figé :
 * KPIs du jour + deltas vs hier, CA 7 jours + delta vs semaine précédente, top produits,
 * alertes réelles (rupture stock, commandes à accepter, note Google).
 */
export default function DashboardOverview({ tenant }: { tenant: Tenant }) {
  const [orders, setOrders] = useState<Order[]>([]);
  const [itemsByOrder, setItemsByOrder] = useState<Record<string, OrderItem[]>>({});
  const [outOfStock, setOutOfStock] = useState<string[]>([]);
  const currency = tenant.currency;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { orders: list, itemsByOrder: items } = await loadOrders(tenant.id);
      if (cancelled) return;
      setOrders(list);
      setItemsByOrder(items);
      const { data } = await supabase
        .from('products')
        .select('name, is_available')
        .eq('tenant_id', tenant.id);
      if (cancelled) return;
      setOutOfStock(((data as { name: string; is_available: boolean }[]) ?? [])
        .filter((p) => p.is_available === false)
        .map((p) => p.name));
    })();
    return () => { cancelled = true; };
  }, [tenant.id]);

  const s: DashboardStats = useMemo(
    () => computeDashboardStats(orders, itemsByOrder, outOfStock),
    [orders, itemsByOrder, outOfStock],
  );
  const fmt = (c: number) => formatPrice(c, currency);
  const maxRev = Math.max(1, ...s.last7.map((d) => d.revenueCents));
  const maxTop = Math.max(1, ...s.topProducts.map((p) => p.qty));
  const noAlert = s.outOfStock.length === 0 && s.toAccept === 0 && !tenant.rating_google;

  return (
    <div className="flex flex-col gap-5">
      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Tableau de bord</div>
        <h2 className="text-2xl font-black text-slate-900">Voici ce qui se passe chez {tenant.name} aujourd'hui.</h2>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Kpi icon="🧾" label="Commandes du jour" value={String(s.todayCount)} delta={deltaText(s.dOrders)} positive={!((s.dOrders ?? 0) < 0)} />
        <Kpi icon="💰" label="CA du jour" value={fmt(s.todayRevenueCents)} delta={deltaText(s.dRevenue)} positive={!((s.dRevenue ?? 0) < 0)} />
        <Kpi icon="🍳" label="En cours" value={String(s.inProgress)} sub="à préparer / à retirer" highlight={s.inProgress > 0} />
        <Kpi icon="🛒" label="Panier moyen" value={fmt(s.avgBasketCents)} delta={deltaText(s.dAvg)} positive={!((s.dAvg ?? 0) < 0)} />
      </div>

      {/* CA 7 jours + top produits */}
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="rounded-2xl border border-slate-200 bg-white p-6 lg:col-span-2">
          <div className="mb-3 flex items-center justify-between">
            <div>
              <div className="text-xs font-bold uppercase tracking-wider text-slate-500">CA des 7 derniers jours</div>
              <div className="mt-1 text-2xl font-black text-slate-900">{fmt(s.revenue7dCents)}</div>
            </div>
            {s.d7 != null && (
              <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${s.d7 >= 0 ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
                {s.d7 >= 0 ? '↑' : '↓'} {Math.abs(s.d7)}% vs sem. préc.
              </span>
            )}
          </div>
          <div className="flex h-40 items-end gap-2">
            {s.last7.map((d, i) => (
              <div key={i} className="flex flex-1 flex-col items-center gap-1">
                <div className="flex w-full flex-1 items-end">
                  <div className="w-full rounded-t" style={{ height: `${(d.revenueCents / maxRev) * 100}%`, minHeight: d.revenueCents > 0 ? 4 : 0, background: tenant.primary_color || '#DC2626' }} />
                </div>
                <span className="text-[10px] text-slate-400">{d.label}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <div className="mb-3 text-xs font-bold uppercase tracking-wider text-slate-500">Top produits — 7 jours</div>
          {s.topProducts.length === 0 && <div className="text-sm text-slate-400">Aucune vente pour l'instant.</div>}
          <div className="space-y-3">
            {s.topProducts.map((p, i) => (
              <div key={p.name}>
                <div className="mb-1 flex items-center justify-between text-sm">
                  <span className="flex min-w-0 items-center gap-2">
                    <span className="w-4 text-xs font-bold text-slate-400">#{i + 1}</span>
                    <span className="truncate font-semibold text-slate-700">{p.name}</span>
                  </span>
                  <span className="font-bold text-slate-900">{p.qty}</span>
                </div>
                <div className="h-1.5 overflow-hidden rounded-full bg-slate-100">
                  <div className="h-full rounded-full" style={{ width: `${(p.qty / maxTop) * 100}%`, background: tenant.primary_color || '#DC2626' }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Alertes RÉELLES */}
      <div className="grid gap-4 md:grid-cols-3">
        {s.outOfStock.length > 0 && (
          <AlertCard tone="amber" icon="⚠️" title="Produit en rupture"
            desc={`${s.outOfStock.length} produit${s.outOfStock.length > 1 ? 's' : ''} indisponible${s.outOfStock.length > 1 ? 's' : ''} : ${s.outOfStock.slice(0, 3).join(', ')}${s.outOfStock.length > 3 ? '…' : ''}.`} />
        )}
        {s.toAccept > 0 && (
          <AlertCard tone="blue" icon="🔔" title="Commandes à accepter"
            desc={`${s.toAccept} commande${s.toAccept > 1 ? 's' : ''} en attente d'acceptation. Traitez-les vite.`} />
        )}
        {tenant.rating_google ? (
          <AlertCard tone="green" icon="✨" title={`Note Google : ${tenant.rating_google} ⭐`}
            desc="Pensez à répondre à vos avis pour soigner votre e-réputation." />
        ) : null}
        {noAlert && (
          <div className="py-6 text-center text-sm text-slate-400 md:col-span-3">✅ Rien à signaler — tout est sous contrôle.</div>
        )}
      </div>
    </div>
  );
}

function Kpi({ icon, label, value, sub, delta, positive, highlight }: {
  icon: string; label: string; value: string; sub?: string; delta?: string; positive?: boolean; highlight?: boolean;
}) {
  return (
    <div className={`rounded-2xl border bg-white p-5 ${highlight ? 'border-red-300 ring-2 ring-red-100' : 'border-slate-200'}`}>
      <div className="mb-2 flex items-center justify-between">
        <div className="text-xl">{icon}</div>
        {delta ? (
          <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${positive ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
            {positive ? '↑' : '↓'} {delta}
          </span>
        ) : null}
      </div>
      <div className="text-xs font-bold uppercase tracking-wider text-slate-500">{label}</div>
      <div className="mt-1 text-2xl font-black text-slate-900">{value}</div>
      {sub ? <div className="mt-1 text-xs text-slate-500">{sub}</div> : null}
    </div>
  );
}

function AlertCard({ tone, icon, title, desc }: { tone: 'amber' | 'blue' | 'green'; icon: string; title: string; desc: string }) {
  const tones = {
    amber: 'bg-amber-50 border-amber-200 text-amber-900',
    blue: 'bg-blue-50 border-blue-200 text-blue-900',
    green: 'bg-emerald-50 border-emerald-200 text-emerald-900',
  };
  return (
    <div className={`rounded-xl border p-4 ${tones[tone]}`}>
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 text-xl">{icon}</div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-bold">{title}</div>
          <div className="mt-1 text-xs leading-relaxed opacity-90">{desc}</div>
        </div>
      </div>
    </div>
  );
}
